import { useEffect, useRef } from 'react';
import maplibregl, { type StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

/** Free OpenStreetMap raster tiles; replace with VITE_MAP_STYLE_URL (any MapLibre style) in production. */
const OSM_STYLE: StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      maxzoom: 19,
      attribution: '© OpenStreetMap',
    },
  },
  layers: [
    { id: 'bg', type: 'background', paint: { 'background-color': '#eef0ec' } },
    { id: 'osm', type: 'raster', source: 'osm', paint: { 'raster-saturation': -0.35, 'raster-contrast': -0.05 } },
  ],
};

const styleUrl = import.meta.env.VITE_MAP_STYLE_URL as string | undefined;

const ICONS = {
  restaurant:
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M15 11h.01M11 15h.01M16 16h.01M2 16l20 6-6-20A20 20 0 0 0 2 16"/><path d="M5.71 17.11a17.04 17.04 0 0 1 11.4-11.4"/></svg>',
  home: '<svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M3 10.5 12 3l9 7.5V21a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1z"/></svg>',
  driver:
    '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18.5" cy="17.5" r="3.5"/><circle cx="5.5" cy="17.5" r="3.5"/><circle cx="15" cy="5" r="1"/><path d="M12 17.5V14l-3-3 4-3 2 3h2"/></svg>',
};

export interface MapMarker {
  id: string;
  kind: keyof typeof ICONS;
  lat: number;
  lng: number;
  label?: string;
  headingDeg?: number | null;
}

interface Props {
  center: { lat: number; lng: number };
  zoom?: number;
  markers: MapMarker[];
  route?: { lat: number; lng: number }[];
  /** Fit bounds to the markers whenever this key changes. */
  fitKey?: string;
  onMapClick?: (p: { lat: number; lng: number }) => void;
  className?: string;
  testId?: string;
}

export function MapView({ center, zoom = 13, markers, route, fitKey, onMapClick, className, testId }: Props) {
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const markerRefs = useRef(new Map<string, maplibregl.Marker>());
  const clickRef = useRef(onMapClick);
  clickRef.current = onMapClick;
  const ready = useRef(false);

  useEffect(() => {
    if (!el.current) return;
    let m: maplibregl.Map;
    try {
      m = new maplibregl.Map({
        container: el.current,
        style: styleUrl || OSM_STYLE,
        center: [center.lng, center.lat],
        zoom,
        attributionControl: { compact: true },
        cooperativeGestures: false,
      });
    } catch {
      return; // WebGL unavailable: markers stay in the DOM list below
    }
    m.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
    m.on('click', (e) => clickRef.current?.({ lat: e.lngLat.lat, lng: e.lngLat.lng }));
    m.on('load', () => {
      ready.current = true;
      m.addSource('route', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      m.addLayer({ id: 'route-casing', type: 'line', source: 'route', paint: { 'line-color': '#ffffff', 'line-width': 9 }, layout: { 'line-cap': 'round', 'line-join': 'round' } });
      m.addLayer({ id: 'route', type: 'line', source: 'route', paint: { 'line-color': '#e93434', 'line-width': 5 }, layout: { 'line-cap': 'round', 'line-join': 'round' } });
      updateRoute();
    });
    map.current = m;
    const refs = markerRefs.current;
    return () => {
      refs.forEach((mk) => mk.remove());
      refs.clear();
      m.remove();
      map.current = null;
      ready.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateRoute() {
    const m = map.current;
    if (!m || !ready.current) return;
    const src = m.getSource('route') as maplibregl.GeoJSONSource | undefined;
    src?.setData({
      type: 'FeatureCollection',
      features: route && route.length > 1 ? [{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: route.map((p) => [p.lng, p.lat]) } }] : [],
    });
  }

  useEffect(updateRoute, [route]);

  // Sync markers (create, move, remove) without re-creating the map.
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    const seen = new Set<string>();
    for (const mk of markers) {
      seen.add(mk.id);
      let marker = markerRefs.current.get(mk.id);
      if (!marker) {
        const node = document.createElement('div');
        node.className = `marker marker-${mk.kind}`;
        node.dataset.testid = `marker-${mk.kind}`;
        node.innerHTML = ICONS[mk.kind] + (mk.label ? `<span class="marker-label"></span>` : '');
        marker = new maplibregl.Marker({ element: node, anchor: 'center' }).setLngLat([mk.lng, mk.lat]).addTo(m);
        markerRefs.current.set(mk.id, marker);
      } else {
        marker.setLngLat([mk.lng, mk.lat]);
      }
      const node = marker.getElement();
      node.dataset.lat = String(mk.lat);
      node.dataset.lng = String(mk.lng);
      const label = node.querySelector('.marker-label');
      if (label) label.textContent = mk.label ?? '';
    }
    for (const [id, marker] of markerRefs.current) {
      if (!seen.has(id)) {
        marker.remove();
        markerRefs.current.delete(id);
      }
    }
  }, [markers]);

  useEffect(() => {
    const m = map.current;
    if (!m || markers.length === 0) return;
    if (markers.length === 1) {
      m.easeTo({ center: [markers[0].lng, markers[0].lat], zoom: Math.max(m.getZoom(), 14) });
      return;
    }
    const b = new maplibregl.LngLatBounds();
    markers.forEach((mk) => b.extend([mk.lng, mk.lat]));
    m.fitBounds(b, { padding: 70, maxZoom: 15, duration: 600 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey]);

  return (
    <div className={`map ${className ?? ''}`} data-testid={testId}>
      <div ref={el} style={{ position: 'absolute', inset: 0 }} />
    </div>
  );
}
